import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import Swal from "sweetalert2";
import "moment/locale/th";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import ApiConfig from "../../common/config/ApiConfig";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Step1 from "./PageComponents/Agent/Add/Step1";
import Step2 from "./PageComponents/Agent/Add/Step2";
import Step3 from "./PageComponents/Agent/Add/Step3";
import Step4 from "./PageComponents/Agent/Add/Step4";
import { useLanguage } from "../../common/i18n/LanguageContext";

function Limitnumber() {
  const { t } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const levelurl = searchParams.get("level") ?? "";
  const uplineusername = searchParams.get("uplineusername") ?? "";

  const [currentComponent, setCurrentComponent] = useState(1);
  const [selectedLotto, setSelectedLotto] = useState("thai");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pos = localStorage.getItem("position");
  const user =
    uplineusername !== "" ? uplineusername : localStorage.getItem("user");
  // Only an owner adding directly (no upline chosen in the URL) picks the
  // currency. Adding under a chosen upline (e.g. ?level=master&uplineusername=
  // MS01) always takes that upline's currency, and anyone else takes their own.
  const canPickCurrency = pos === "owner" && uplineusername === "";
  const [position, setPosition] = useState(levelurl);
  const prefix =
    pos === "owner" ? (uplineusername !== "" ? uplineusername : "MS") : user;
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [tel, setTel] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("active");
  const [lock, setLock] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [currencyId, setCurrencyId] = useState(1);
  const [suffix1, setSuffix1] = useState(0);
  const [suffix2, setSuffix2] = useState(0);
  const [isThaiTableVisible] = useState(true);

  // Maps LottoType.type (the raw string this page's API calls use, e.g.
  // "th_gov") to the short key each Step component's `show(key)` filter
  // expects — the two vocabularies predate each other and don't line up.
  const LOTTO_TYPE_KEY_BY_TYPE = {
    th_gov: "thai",
    hanoy_PhuQua: "hanoi",
    laos_gov: "laos",
    Yeekee_5: "yeekee",
    Yeekee_10: "yeekeevip",
    Yeekee_15: "yeekeevvip",
  };

  const [lottoTypes, setLottoTypes] = useState([]);
  const getLottoTypes = async () => {
    await ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
  };

  const [packages, setPackages] = useState([]);
  // Same package-list endpoint AgentEdit.jsx now uses; `user` here is
  // never a real target agent (this agent doesn't exist yet), so the
  // per-package `status` this returns isn't used — every package still
  // starts unassigned (enabled: false) via DEFAULT_PACKAGE_DATA, same as
  // before.
  const getPackages = async () => {
    await ApiConfig.get(
      `/getAgentPackageList?username=${encodeURIComponent(user)}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setPackages(Array.isArray(result.data) ? result.data : []);
    });
  };

  // All reward-type groups, every lotto type — fetched once. Step1/Step2's
  // display and the submit payload all derive their per-lotto-type slice
  // from this by filtering on group.lottoType.type, instead of re-fetching
  // per filter change or trusting a hardcoded stake list.
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);
  const getRewardTypeGroups = async () => {
    await ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
  };

  // rewardTypes.code list for a given raw LottoType.type (e.g. "th_gov"),
  // across every group that belongs to it.
  const getRewardTypeCodes = (lottoTypeRaw) =>
    rewardTypeGroups
      .filter((g) => g.lottoType?.type === lottoTypeRaw)
      .flatMap((g) => (g.rewardTypes || []).map((rt) => rt.code));

  // Builds a commission/payrate/maxbet/minbet/maxbetpermatch submit object
  // from the reward-type codes that actually belong to this lotto type,
  // instead of forwarding whatever keys happen to be sitting in state. No
  // table uses an `_mcms` suffix (adregister's backend was modernized to
  // match updateOwner/updateAgent — see user.service.ts), so read/write key
  // is always the bare code.
  const buildRateObjFromGroups = (lottoTypeRaw, stateObj) => {
    const codes = getRewardTypeCodes(lottoTypeRaw);
    const result = {};
    codes.forEach((code) => {
      result[code] = Number(stateObj[code] ?? 0);
    });
    return result;
  };

  const activeRewardTypeGroups = rewardTypeGroups.filter(
    (g) => LOTTO_TYPE_KEY_BY_TYPE[g.lottoType?.type] === selectedLotto,
  );

  // Step 1: Commission & Payrate — now one independent set per package
  // (Step1 is rendered once per package card, toggled open/closed), keyed
  // by package id then by the short lotto key (thai/hanoi/laos/yeekee/
  // yeekeevip/yeekeevvip). *Upline holds the CREATING admin's own current
  // rate for that package/lotto, shown as a cap/hint — fetched per package
  // since DownlineCommission/Payrate rows are package-scoped.
  // packagesData[pkgId] = {
  //   enabled: boolean,
  //   commission: { thai: {...}, hanoi: {...}, ... },
  //   payrate: { thai: {...}, hanoi: {...}, ... },
  //   commissionUpline: { thai: {...}, ... },
  //   payrateUpline: { thai: {...}, ... },
  // }
  const [packagesData, setPackagesData] = useState({});

  const EMPTY_LOTTO_MAP = {
    thai: {},
    hanoi: {},
    laos: {},
    yeekee: {},
    yeekeevip: {},
    yeekeevvip: {},
  };
  const DEFAULT_PACKAGE_DATA = {
    enabled: false,
    commission: EMPTY_LOTTO_MAP,
    payrate: EMPTY_LOTTO_MAP,
    commissionUpline: EMPTY_LOTTO_MAP,
    payrateUpline: EMPTY_LOTTO_MAP,
  };
  const getPkgData = (pkgId) => packagesData[pkgId] ?? DEFAULT_PACKAGE_DATA;

  const togglePackageEnabled = (pkgId) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      return {
        ...prev,
        [pkgId]: { ...current, enabled: !current.enabled },
      };
    });
  };

  // Whether a package's card body (Step1) is shown — a separate concern
  // from pkgData.enabled (the on/off switch, which controls the `status`
  // sent to the backend). Accordion behavior: at most one package expanded
  // at a time, defaulting to none (null) — opening one collapses whichever
  // was open before, and clicking the open one again closes it.
  const [expandedPackageId, setExpandedPackageId] = useState(null);
  const isPackageExpanded = (pkgId) => expandedPackageId === pkgId;
  const togglePackageExpanded = (pkgId) => {
    setExpandedPackageId((prev) => (prev === pkgId ? null : pkgId));
  };

  // Factory for Step1's setCommission/setPayrateUpdate props, scoped to one
  // package + one lotto key. Step1 calls these both as a plain new object
  // AND as a functional updater `(prev) => next` (its group-sync cascade
  // uses the latter), so this must support both call styles like a normal
  // useState setter does.
  const makePkgFieldSetter = (pkgId, field, lottoKey) => (valueOrFn) => {
    setPackagesData((prev) => {
      const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
      const currentValue = current[field][lottoKey] ?? {};
      const nextValue =
        typeof valueOrFn === "function" ? valueOrFn(currentValue) : valueOrFn;
      return {
        ...prev,
        [pkgId]: {
          ...current,
          [field]: { ...current[field], [lottoKey]: nextValue },
        },
      };
    });
  };

  // Step 2: Bet Limit
  const [Thaimaxbetupdate, setThaimaxbetupdate] = useState({});
  const [Hanoimaxbetupdate, setHanoimaxbetupdate] = useState({});
  const [Laosmaxbetupdate, setLaosmaxbetupdate] = useState({});
  const [Yeekeemaxbetupdate, setYeekeemaxbetupdate] = useState({});
  const [Yeekeevipmaxbetupdate, setYeekeevipmaxbetupdate] = useState({});
  const [Yeekeevvipmaxbetupdate, setYeekeevvipmaxbetupdate] = useState({});
  const [ThaimaxbetUpline, setThaimaxbetUpline] = useState({});
  const [HanoimaxbetUpline, setHanoimaxbetUpline] = useState({});
  const [LaosmaxbetUpline, setLaosmaxbetUpline] = useState({});
  const [YeekeemaxbetUpline, setYeekeemaxbetUpline] = useState({});
  const [YeekeevipmaxbetUpline, setYeekeevipmaxbetUpline] = useState({});
  const [YeekeevvipmaxbetUpline, setYeekeevvipmaxbetUpline] = useState({});
  const [Thaiminbetupdate, setThaiminbetupdate] = useState({});
  const [Hanoiminbetupdate, setHanoiminbetupdate] = useState({});
  const [Laosminbetupdate, setLaosminbetupdate] = useState({});
  const [Yeekeeminbetupdate, setYeekeeminbetupdate] = useState({});
  const [Yeekeevipminbetupdate, setYeekeevipminbetupdate] = useState({});
  const [Yeekeevvipminbetupdate, setYeekeevvipminbetupdate] = useState({});
  const [ThaiminbetUpline, setThaiminbetUpline] = useState({});
  const [HanoiminbetUpline, setHanoiminbetUpline] = useState({});
  const [LaosminbetUpline, setLaosminbetUpline] = useState({});
  const [YeekeeminbetUpline, setYeekeeminbetUpline] = useState({});
  const [YeekeevipminbetUpline, setYeekeevipminbetUpline] = useState({});
  const [YeekeevvipminbetUpline, setYeekeevvipminbetUpline] = useState({});
  const [Thaipermatchupdate, setThaipermatchupdate] = useState({});
  const [Hanoipermatchupdate, setHanoipermatchupdate] = useState({});
  const [Laospermatchupdate, setLaospermatchupdate] = useState({});
  const [Yeekeepermatchupdate, setYeekeepermatchupdate] = useState({});
  const [Yeekeevippermatchupdate, setYeekeevippermatchupdate] = useState({});
  const [Yeekeevvippermatchupdate, setYeekeevvippermatchupdate] = useState({});
  const [ThaipermatchUpline, setThaipermatchUpline] = useState({});
  const [HanoipermatchUpline, setHanoipermatchUpline] = useState({});
  const [LaospermatchUpline, setLaospermatchUpline] = useState({});
  const [YeekeepermatchUpline, setYeekeepermatchUpline] = useState({});
  const [YeekeevippermatchUpline, setYeekeevippermatchUpline] = useState({});
  const [YeekeevvippermatchUpline, setYeekeevvippermatchUpline] = useState({});

  // Step 3: Max Fight %
  const [Thaimaxfight, setThaimaxfight] = useState({});
  const [Hanoimaxfight, setHanoimaxfight] = useState({});
  const [Laosmaxfight, setLaosmaxfight] = useState({});
  const [Yeekeemaxfight, setYeekeemaxfight] = useState({});
  const [Yeekeevipmaxfight, setYeekeevipmaxfight] = useState({});
  const [Yeekeevvipmaxfight, setYeekeevvipmaxfight] = useState({});
  const [ThaimaxfightUpline, setThaimaxfightUpline] = useState({});
  const [HanoimaxfightUpline, setHanoimaxfightUpline] = useState({});
  const [LaosmaxfightUpline, setLaosmaxfightUpline] = useState({});
  const [YeekeemaxfightUpline, setYeekeemaxfightUpline] = useState({});
  const [YeekeevipmaxfightUpline, setYeekeevipmaxfightUpline] = useState({});
  const [YeekeevvipmaxfightUpline, setYeekeevvipmaxfightUpline] = useState({});

  // Step 4: Fight Limit
  const [Thaimaxfightlimitupdate, setThaimaxfightlimitupdate] = useState({});
  const [Hanoimaxfightlimitupdate, setHanoimaxfightlimitupdate] = useState({});
  const [Laosmaxfightlimitupdate, setLaosmaxfightlimitupdate] = useState({});
  const [Yeekeemaxfightlimitupdate, setYeekeemaxfightlimitupdate] = useState(
    {},
  );
  const [Yeekeevipmaxfightlimitupdate, setYeekeevipmaxfightlimitupdate] =
    useState({});
  const [Yeekeevvipmaxfightlimitupdate, setYeekeevvipmaxfightlimitupdate] =
    useState({});
  const [ThaimaxfightlimitUpline, setThaimaxfightlimitUpline] = useState({});
  const [HanoimaxfightlimitUpline, setHanoimaxfightlimitUpline] = useState({});
  const [LaosmaxfightlimitUpline, setLaosmaxfightlimitUpline] = useState({});
  const [YeekeemaxfightlimitUpline, setYeekeemaxfightlimitUpline] = useState(
    {},
  );
  const [YeekeevipmaxfightlimitUpline, setYeekeevipmaxfightlimitUpline] =
    useState({});
  const [YeekeevvipmaxfightlimitUpline, setYeekeevvipmaxfightlimitUpline] =
    useState({});

  const fightpercentandcommissionget = async (pkgId) => {
    const params = new URLSearchParams({ agent: user, package_id: pkgId });
    ApiConfig.get(`/getfightpercentandcommission?${params.toString()}`)
      .then((result) => {
        if (result.resCode !== 200) {
          toast.error(result.message || t("common_load_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return {
            commissionArray: [],
            payrateArray: [],
            fightpercentArray: [],
          };
        }
        const list = Array.isArray(result.data) ? result.data : [];
        const commissionArray = list.filter((obj) => "commission" in obj);
        const payrateArray = list.filter((obj) => "payrate" in obj);
        const fightpercentArray = list.filter((obj) => "fightpercent" in obj);
        return { commissionArray, payrateArray, fightpercentArray };
      })
      .then(({ commissionArray, payrateArray, fightpercentArray }) => {
        if (!payrateArray?.[0] || !commissionArray?.[0]) return;

        // These rows are the CREATING admin's own commission/payrate/
        // fight-percent — the cap the new agent can't exceed — one row per
        // (lotto_type, stake), e.g. { lotto_type: "th_gov", stake: "first",
        // commission: "30.00" }, not one row per lotto_type with named
        // fields on it.
        const cmRows = Array.isArray(commissionArray[0]?.commission)
          ? commissionArray[0].commission
          : [];
        const getComm = (lottoType, stake) => {
          const row = cmRows.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.commission) : 0;
        };
        const prRows = Array.isArray(payrateArray[0]?.payrate)
          ? payrateArray[0].payrate
          : [];
        const getPay = (lottoType, stake) => {
          const row = prRows.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.payrate) : 0;
        };
        const fpO = Array.isArray(fightpercentArray[0]?.fightpercent)
          ? fightpercentArray[0].fightpercent
          : [];
        const getFightPercent = (lottoType, stake) => {
          const row = fpO.find(
            (obj) => obj.lotto_type === lottoType && obj.stake === stake,
          );
          return row ? Number(row.fight_percent_limit) : 0;
        };

        const buildFetchedRateObj = (lottoTypeRaw, getter) => {
          const result = {};
          getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
            result[code] = getter(lottoTypeRaw, code);
          });
          return result;
        };

        // Step1's caps are package-scoped — write into this package's own
        // slot in packagesData, not shared top-level state.
        setPackagesData((prev) => {
          const current = prev[pkgId] ?? DEFAULT_PACKAGE_DATA;
          return {
            ...prev,
            [pkgId]: {
              ...current,
              commissionUpline: {
                thai: buildFetchedRateObj("th_gov", getComm),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getComm),
                laos: buildFetchedRateObj("laos_gov", getComm),
                yeekee: buildFetchedRateObj("Yeekee_5", getComm),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getComm),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getComm),
              },
              payrateUpline: {
                thai: buildFetchedRateObj("th_gov", getPay),
                hanoi: buildFetchedRateObj("hanoy_PhuQua", getPay),
                laos: buildFetchedRateObj("laos_gov", getPay),
                yeekee: buildFetchedRateObj("Yeekee_5", getPay),
                yeekeevip: buildFetchedRateObj("Yeekee_10", getPay),
                yeekeevvip: buildFetchedRateObj("Yeekee_15", getPay),
              },
            },
          };
        });
        // Step3's fight-percent cap is NOT package-scoped (FightPercentLimit
        // has no package_id column) — shared top-level state is fine even
        // though this runs once per package (same value every time).
        setThaimaxfightUpline(buildFetchedRateObj("th_gov", getFightPercent));
        setHanoimaxfightUpline(
          buildFetchedRateObj("hanoy_PhuQua", getFightPercent),
        );
        setLaosmaxfightUpline(buildFetchedRateObj("laos_gov", getFightPercent));
        setYeekeemaxfightUpline(
          buildFetchedRateObj("Yeekee_5", getFightPercent),
        );
        setYeekeevipmaxfightUpline(
          buildFetchedRateObj("Yeekee_10", getFightPercent),
        );
        setYeekeevvipmaxfightUpline(
          buildFetchedRateObj("Yeekee_15", getFightPercent),
        );
      });
  };

  const fightlimitget = async () => {
    await ApiConfig.get(
      `/getfightlimit?agent=${encodeURIComponent(user)}${canPickCurrency ? `&currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;

      // FightLimit rows are the CREATING admin's own rows — one row per
      // (lotto_type, stake), e.g. { lotto_type: "th_gov", stake: "first",
      // fight_limit: "1000.00" }.
      const ownRows = Array.isArray(result.data?.fightlimt)
        ? result.data.fightlimt
        : [];
      const buildObj = (lottoTypeRaw) => {
        const built = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          built[code] = row ? Number(row.fight_limit) : 0;
        });
        return built;
      };

      setThaimaxfightlimitUpline(buildObj("th_gov"));
      setHanoimaxfightlimitUpline(buildObj("hanoy_PhuQua"));
      setLaosmaxfightlimitUpline(buildObj("laos_gov"));
      setYeekeemaxfightlimitUpline(buildObj("Yeekee_5"));
      setYeekeevipmaxfightlimitUpline(buildObj("Yeekee_10"));
      setYeekeevvipmaxfightlimitUpline(buildObj("Yeekee_15"));
    });
  };

  const maxbetget = async () => {
    await ApiConfig.get(
      `/Getmaxbet?agentuser=${encodeURIComponent(user)}${canPickCurrency ? `&currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBet rows are the CREATING admin's own rows — one row per
      // (lotto_type, stake).
      const ownRows = Array.isArray(result.data?.maxbet)
        ? result.data.maxbet
        : [];
      const buildObj = (lottoTypeRaw) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbet) : 0;
        });
        return result;
      };

      setThaimaxbetUpline(buildObj("th_gov"));
      setHanoimaxbetUpline(buildObj("hanoy_PhuQua"));
      setLaosmaxbetUpline(buildObj("laos_gov"));
      setYeekeemaxbetUpline(buildObj("Yeekee_5"));
      setYeekeevipmaxbetUpline(buildObj("Yeekee_10"));
      setYeekeevvipmaxbetUpline(buildObj("Yeekee_15"));
    });
  };

  const minbetget = async () => {
    await ApiConfig.get(
      `/Getminbet?agentuser=${encodeURIComponent(user)}${canPickCurrency ? `&currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MinBet rows are the CREATING admin's own rows — one row per
      // (lotto_type, stake).
      const ownRows = Array.isArray(result.data?.minbet)
        ? result.data.minbet
        : [];
      const buildObj = (lottoTypeRaw) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.minbet) : 0;
        });
        return result;
      };

      setThaiminbetUpline(buildObj("th_gov"));
      setHanoiminbetUpline(buildObj("hanoy_PhuQua"));
      setLaosminbetUpline(buildObj("laos_gov"));
      setYeekeeminbetUpline(buildObj("Yeekee_5"));
      setYeekeevipminbetUpline(buildObj("Yeekee_10"));
      setYeekeevvipminbetUpline(buildObj("Yeekee_15"));
    });
  };

  const maxpermatchtget = async () => {
    await ApiConfig.get(
      `/Getmaxbetpermatch?agentuser=${encodeURIComponent(user)}${canPickCurrency ? `&currency_id=${currencyId}` : ""}`,
    ).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      if (!result.data) return;
      // MaxBetPerMatch rows are the CREATING admin's own rows — one row per
      // (lotto_type, stake).
      const ownRows = Array.isArray(result.data?.maxbetpermatch)
        ? result.data.maxbetpermatch
        : [];
      const buildObj = (lottoTypeRaw) => {
        const result = {};
        getRewardTypeCodes(lottoTypeRaw).forEach((code) => {
          const row = ownRows.find(
            (obj) => obj.lotto_type === lottoTypeRaw && obj.stake === code,
          );
          result[code] = row ? Number(row.maxbetpermatch) : 0;
        });
        return result;
      };

      setThaipermatchUpline(buildObj("th_gov"));
      setHanoipermatchUpline(buildObj("hanoy_PhuQua"));
      setLaospermatchUpline(buildObj("laos_gov"));
      setYeekeepermatchUpline(buildObj("Yeekee_5"));
      setYeekeevippermatchUpline(buildObj("Yeekee_10"));
      setYeekeevvippermatchUpline(buildObj("Yeekee_15"));
    });
  };

  // The currency list is needed by everyone (to label the upline's limits);
  // only an owner picks one — the backend ignores it for anyone else and uses
  // the caller's own, which is then the upline's currency (`user` below).
  const [uplineCurrencyId, setUplineCurrencyId] = useState(null);
  useEffect(() => {
    ApiConfig.get("/getCurrencyList").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      const list = Array.isArray(result.data) ? result.data : [];
      setCurrencies(list);
      if (canPickCurrency && list.length > 0 && !list.some((c) => c.id === 1)) {
        setCurrencyId(list[0].id);
      }
    });
    if (!canPickCurrency) {
      ApiConfig.get(`/agentprofile?user=${encodeURIComponent(user)}`).then(
        (result) => {
          if (result.resCode === 200) {
            setUplineCurrencyId(result.data?.currency_id ?? null);
          }
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The upline limits shown in Step 2/4 are read in this currency.
  const uplineCurrencySymbol =
    currencies.find(
      (c) => c.id === (canPickCurrency ? currencyId : uplineCurrencyId),
    )?.symbol ?? "";

  useEffect(() => {
    getLottoTypes();
    getRewardTypeGroups();
    getPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fightpercentandcommissionget/fightlimitget/maxbetget/minbetget/
  // maxpermatchtget all build their state from rewardTypeGroups' codes, so
  // they have to wait for that fetch to land — calling them from the mount
  // effect above would close over rewardTypeGroups' still-empty initial
  // value and populate everything as 0.
  useEffect(() => {
    if (rewardTypeGroups.length > 0) {
      fightlimitget();
      maxbetget();
      minbetget();
      maxpermatchtget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups, currencyId]);

  // Fetch each package's own upline-cap once the package list and reward
  // groups are both ready — one call per package (Step1 shows every
  // package as its own card, not a single selected one anymore).
  useEffect(() => {
    if (rewardTypeGroups.length > 0 && packages.length > 0) {
      packages.forEach((pkg) => fightpercentandcommissionget(pkg.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups, packages]);

  const handleChangeObj = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 100) maxvalue = 100;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangeFightLimit = (event, obj, setfunction) => {
    let maxvalue = event.target.value;
    if (maxvalue > 10000) maxvalue = 10000;
    setfunction({ ...obj, [event.target.name]: Number(maxvalue) });
  };

  const handleChangepayrateObj = (event, obj, setfunction) => {
    setfunction({ ...obj, [event.target.name]: Number(event.target.value) });
  };

  const handleNext = () => {
    if (currentComponent < 4) setCurrentComponent(currentComponent + 1);
  };

  const handlePrevious = () => {
    if (currentComponent > 1) setCurrentComponent(currentComponent - 1);
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      Swal.fire({
        icon: "error",
        title: t("common_oops"),
        text: t("agentadd_password_required"),
      });
      return;
    } else if (!position) {
      Swal.fire({
        icon: "error",
        title: t("common_oops"),
        text: t("agentadd_position_required"),
      });
      return;
    } else if (!user) {
      Swal.fire({
        icon: "error",
        title: t("common_oops"),
        text: t("agentadd_generic_error"),
      });
      return;
    }

    const enabledPackages = packages.filter(
      (pkg) => getPkgData(pkg.id).enabled,
    );
    if (enabledPackages.length === 0) {
      Swal.fire({
        icon: "error",
        title: t("common_oops"),
        text: t("agentadd_package_required"),
      });
      return;
    }

    const confirmResult = await Swal.fire({
      icon: "warning",
      title: t("agentadd_confirm_title"),
      text: `${t("agentadd_confirm_text_prefix")} "${prefix}${suffix1}${suffix2}" ${t("agentadd_confirm_text_middle")} "${position}"?`,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("common_yes"),
      cancelButtonText: t("common_no"),
    });
    if (!confirmResult.isConfirmed) return;

    const Username = `${prefix}${suffix1}${suffix2}`;
    const json = {
      Username: Username,
      Password: password,
      credit: 0,
      position: position,
      UplineUsername: uplineusername !== "" ? uplineusername : user,
      name: name,
      tel: tel,
      email: email,
      status: status,
      lock: lock,
      ...(canPickCurrency ? { currency_id: currencyId } : {}),
      // Every package is sent, not just the ones toggled on — a disabled
      // card still creates an AdminPackage row (status: false) and its
      // zeroed-out DownlineCommission/Payrate rows, it's just inactive.
      packages: packages.map((pkg) => {
        const pkgData = getPkgData(pkg.id);
        return {
          id: pkg.id,
          status: pkgData.enabled,
          commissions: {
            th_commission: buildRateObjFromGroups(
              "th_gov",
              pkgData.commission.thai,
            ),
            hanoi_commission: buildRateObjFromGroups(
              "hanoy_PhuQua",
              pkgData.commission.hanoi,
            ),
            laos_commission: buildRateObjFromGroups(
              "laos_gov",
              pkgData.commission.laos,
            ),
            yeekee5_commission: buildRateObjFromGroups(
              "Yeekee_5",
              pkgData.commission.yeekee,
            ),
            yeekee10_commission: buildRateObjFromGroups(
              "Yeekee_10",
              pkgData.commission.yeekeevip,
            ),
            yeekee15_commission: buildRateObjFromGroups(
              "Yeekee_15",
              pkgData.commission.yeekeevvip,
            ),
          },
          payrates: {
            th_payrate: buildRateObjFromGroups("th_gov", pkgData.payrate.thai),
            hanoi_payrate: buildRateObjFromGroups(
              "hanoy_PhuQua",
              pkgData.payrate.hanoi,
            ),
            laos_payrate: buildRateObjFromGroups(
              "laos_gov",
              pkgData.payrate.laos,
            ),
            yeekee5_payrate: buildRateObjFromGroups(
              "Yeekee_5",
              pkgData.payrate.yeekee,
            ),
            yeekee10_payrate: buildRateObjFromGroups(
              "Yeekee_10",
              pkgData.payrate.yeekeevip,
            ),
            yeekee15_payrate: buildRateObjFromGroups(
              "Yeekee_15",
              pkgData.payrate.yeekeevvip,
            ),
          },
        };
      }),

      th_maxbet: buildRateObjFromGroups("th_gov", Thaimaxbetupdate),
      hanoi_maxbet: buildRateObjFromGroups("hanoy_PhuQua", Hanoimaxbetupdate),
      laos_maxbet: buildRateObjFromGroups("laos_gov", Laosmaxbetupdate),
      yeekee5_maxbet: buildRateObjFromGroups("Yeekee_5", Yeekeemaxbetupdate),
      yeekee10_maxbet: buildRateObjFromGroups(
        "Yeekee_10",
        Yeekeevipmaxbetupdate,
      ),
      yeekee15_maxbet: buildRateObjFromGroups(
        "Yeekee_15",
        Yeekeevvipmaxbetupdate,
      ),

      th_minbet: buildRateObjFromGroups("th_gov", Thaiminbetupdate),
      hanoi_minbet: buildRateObjFromGroups("hanoy_PhuQua", Hanoiminbetupdate),
      laos_minbet: buildRateObjFromGroups("laos_gov", Laosminbetupdate),
      yeekee5_minbet: buildRateObjFromGroups("Yeekee_5", Yeekeeminbetupdate),
      yeekee10_minbet: buildRateObjFromGroups(
        "Yeekee_10",
        Yeekeevipminbetupdate,
      ),
      yeekee15_minbet: buildRateObjFromGroups(
        "Yeekee_15",
        Yeekeevvipminbetupdate,
      ),

      th_maxbetpermatch: buildRateObjFromGroups("th_gov", Thaipermatchupdate),
      hanoi_maxbetpermatch: buildRateObjFromGroups(
        "hanoy_PhuQua",
        Hanoipermatchupdate,
      ),
      laos_maxbetpermatch: buildRateObjFromGroups(
        "laos_gov",
        Laospermatchupdate,
      ),
      yeekee5_maxbetpermatch: buildRateObjFromGroups(
        "Yeekee_5",
        Yeekeepermatchupdate,
      ),
      yeekee10_maxbetpermatch: buildRateObjFromGroups(
        "Yeekee_10",
        Yeekeevippermatchupdate,
      ),
      yeekee15_maxbetpermatch: buildRateObjFromGroups(
        "Yeekee_15",
        Yeekeevvippermatchupdate,
      ),

      th_maxpercent: buildRateObjFromGroups("th_gov", Thaimaxfight),
      hanoi_maxpercent: buildRateObjFromGroups("hanoy_PhuQua", Hanoimaxfight),
      laos_maxpercent: buildRateObjFromGroups("laos_gov", Laosmaxfight),
      yeekee5_maxpercent: buildRateObjFromGroups("Yeekee_5", Yeekeemaxfight),
      yeekee10_maxpercent: buildRateObjFromGroups(
        "Yeekee_10",
        Yeekeevipmaxfight,
      ),
      yeekee15_maxpercent: buildRateObjFromGroups(
        "Yeekee_15",
        Yeekeevvipmaxfight,
      ),

      th_fightlimit: buildRateObjFromGroups("th_gov", Thaimaxfightlimitupdate),
      hanoi_fightlimit: buildRateObjFromGroups(
        "hanoy_PhuQua",
        Hanoimaxfightlimitupdate,
      ),
      laos_fightlimit: buildRateObjFromGroups(
        "laos_gov",
        Laosmaxfightlimitupdate,
      ),
      yeekee5_fightlimit: buildRateObjFromGroups(
        "Yeekee_5",
        Yeekeemaxfightlimitupdate,
      ),
      yeekee10_fightlimit: buildRateObjFromGroups(
        "Yeekee_10",
        Yeekeevipmaxfightlimitupdate,
      ),
      yeekee15_fightlimit: buildRateObjFromGroups(
        "Yeekee_15",
        Yeekeevvipmaxfightlimitupdate,
      ),
    };
    setIsSubmitting(true);
    ApiConfig.post("/adregister", json)
      .then((result) => {
        if (result.resCode === 200) {
          toast.success(
            `${t("agentadd_success_prefix")} ${Username} ${t("agentadd_success_middle")} ${position} ${t("agentadd_success_suffix")}`,
            {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            },
          );
          setTimeout(() => {
            window.location.href = `/agent?level=${levelurl}${uplineusername ? `&username=${uplineusername}` : ""}`;
            setPassword("");
            setName("");
            setTel("");
            setEmail("");
            setStatus("active");
            setLock(false);
            setPosition("");
            setSuffix1(0);
            setSuffix2(0);
            document.documentElement.lang = "th";
          }, 1000);
        } else {
          toast.error(result.message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const STEPS = [
    { label: t("agentadd_step_commission_reward"), step: 1 },
    { label: t("agentadd_step_bet_limit"), step: 2 },
    { label: t("agentadd_step_max_fight_percent"), step: 3 },
    { label: t("agentadd_step_fight_limit"), step: 4 },
  ];

  // Step2/Step3/Step4 aren't package-scoped (unlike Step1's commission/
  // payrate above), so they still resolve one "active" state set from
  // selectedLotto rather than per package.

  // Step2's min/max/max-per-match bet limits.
  const ACTIVE_BETLIMIT_BY_KEY = {
    thai: {
      minbetUpdate: Thaiminbetupdate,
      setMinbetUpdate: setThaiminbetupdate,
      minbetUpline: ThaiminbetUpline,
      maxbetUpdate: Thaimaxbetupdate,
      setMaxbetUpdate: setThaimaxbetupdate,
      maxbetUpline: ThaimaxbetUpline,
      maxbetpermatchUpdate: Thaipermatchupdate,
      setMaxbetpermatchUpdate: setThaipermatchupdate,
      maxbetpermatchUpline: ThaipermatchUpline,
    },
    hanoi: {
      minbetUpdate: Hanoiminbetupdate,
      setMinbetUpdate: setHanoiminbetupdate,
      minbetUpline: HanoiminbetUpline,
      maxbetUpdate: Hanoimaxbetupdate,
      setMaxbetUpdate: setHanoimaxbetupdate,
      maxbetUpline: HanoimaxbetUpline,
      maxbetpermatchUpdate: Hanoipermatchupdate,
      setMaxbetpermatchUpdate: setHanoipermatchupdate,
      maxbetpermatchUpline: HanoipermatchUpline,
    },
    laos: {
      minbetUpdate: Laosminbetupdate,
      setMinbetUpdate: setLaosminbetupdate,
      minbetUpline: LaosminbetUpline,
      maxbetUpdate: Laosmaxbetupdate,
      setMaxbetUpdate: setLaosmaxbetupdate,
      maxbetUpline: LaosmaxbetUpline,
      maxbetpermatchUpdate: Laospermatchupdate,
      setMaxbetpermatchUpdate: setLaospermatchupdate,
      maxbetpermatchUpline: LaospermatchUpline,
    },
    yeekee: {
      minbetUpdate: Yeekeeminbetupdate,
      setMinbetUpdate: setYeekeeminbetupdate,
      minbetUpline: YeekeeminbetUpline,
      maxbetUpdate: Yeekeemaxbetupdate,
      setMaxbetUpdate: setYeekeemaxbetupdate,
      maxbetUpline: YeekeemaxbetUpline,
      maxbetpermatchUpdate: Yeekeepermatchupdate,
      setMaxbetpermatchUpdate: setYeekeepermatchupdate,
      maxbetpermatchUpline: YeekeepermatchUpline,
    },
    yeekeevip: {
      minbetUpdate: Yeekeevipminbetupdate,
      setMinbetUpdate: setYeekeevipminbetupdate,
      minbetUpline: YeekeevipminbetUpline,
      maxbetUpdate: Yeekeevipmaxbetupdate,
      setMaxbetUpdate: setYeekeevipmaxbetupdate,
      maxbetUpline: YeekeevipmaxbetUpline,
      maxbetpermatchUpdate: Yeekeevippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevippermatchupdate,
      maxbetpermatchUpline: YeekeevippermatchUpline,
    },
    yeekeevvip: {
      minbetUpdate: Yeekeevvipminbetupdate,
      setMinbetUpdate: setYeekeevvipminbetupdate,
      minbetUpline: YeekeevvipminbetUpline,
      maxbetUpdate: Yeekeevvipmaxbetupdate,
      setMaxbetUpdate: setYeekeevvipmaxbetupdate,
      maxbetUpline: YeekeevvipmaxbetUpline,
      maxbetpermatchUpdate: Yeekeevvippermatchupdate,
      setMaxbetpermatchUpdate: setYeekeevvippermatchupdate,
      maxbetpermatchUpline: YeekeevvippermatchUpline,
    },
  };
  const activeBetLimit =
    ACTIVE_BETLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_BETLIMIT_BY_KEY.thai;

  // Same idea as ACTIVE_BETLIMIT_BY_KEY above, for Step3's max fight
  // percent.
  const ACTIVE_MAXFIGHT_BY_KEY = {
    thai: {
      maxfight: Thaimaxfight,
      setMaxfight: setThaimaxfight,
      maxfightUpline: ThaimaxfightUpline,
    },
    hanoi: {
      maxfight: Hanoimaxfight,
      setMaxfight: setHanoimaxfight,
      maxfightUpline: HanoimaxfightUpline,
    },
    laos: {
      maxfight: Laosmaxfight,
      setMaxfight: setLaosmaxfight,
      maxfightUpline: LaosmaxfightUpline,
    },
    yeekee: {
      maxfight: Yeekeemaxfight,
      setMaxfight: setYeekeemaxfight,
      maxfightUpline: YeekeemaxfightUpline,
    },
    yeekeevip: {
      maxfight: Yeekeevipmaxfight,
      setMaxfight: setYeekeevipmaxfight,
      maxfightUpline: YeekeevipmaxfightUpline,
    },
    yeekeevvip: {
      maxfight: Yeekeevvipmaxfight,
      setMaxfight: setYeekeevvipmaxfight,
      maxfightUpline: YeekeevvipmaxfightUpline,
    },
  };
  const activeMaxFight =
    ACTIVE_MAXFIGHT_BY_KEY[selectedLotto] ?? ACTIVE_MAXFIGHT_BY_KEY.thai;

  // Same idea as ACTIVE_MAXFIGHT_BY_KEY above, for Step4's fight limit.
  const ACTIVE_FIGHTLIMIT_BY_KEY = {
    thai: {
      fightlimit: Thaimaxfightlimitupdate,
      setFightlimit: setThaimaxfightlimitupdate,
      fightlimitUpline: ThaimaxfightlimitUpline,
    },
    hanoi: {
      fightlimit: Hanoimaxfightlimitupdate,
      setFightlimit: setHanoimaxfightlimitupdate,
      fightlimitUpline: HanoimaxfightlimitUpline,
    },
    laos: {
      fightlimit: Laosmaxfightlimitupdate,
      setFightlimit: setLaosmaxfightlimitupdate,
      fightlimitUpline: LaosmaxfightlimitUpline,
    },
    yeekee: {
      fightlimit: Yeekeemaxfightlimitupdate,
      setFightlimit: setYeekeemaxfightlimitupdate,
      fightlimitUpline: YeekeemaxfightlimitUpline,
    },
    yeekeevip: {
      fightlimit: Yeekeevipmaxfightlimitupdate,
      setFightlimit: setYeekeevipmaxfightlimitupdate,
      fightlimitUpline: YeekeevipmaxfightlimitUpline,
    },
    yeekeevvip: {
      fightlimit: Yeekeevvipmaxfightlimitupdate,
      setFightlimit: setYeekeevvipmaxfightlimitupdate,
      fightlimitUpline: YeekeevvipmaxfightlimitUpline,
    },
  };
  const activeFightLimit =
    ACTIVE_FIGHTLIMIT_BY_KEY[selectedLotto] ?? ACTIVE_FIGHTLIMIT_BY_KEY.thai;

  return (
    <div className="h-screen flex flex-col overflow-hidden mb-10">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("agentadd_header_title")} />
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center justify-center mb-3 mt-2">
              <label className="text-[13px] font-bold mr-2">
                {t("member_commission_filter_label")}
              </label>
              <select
                value={selectedLotto}
                onChange={(e) => setSelectedLotto(e.target.value)}
                className="border-[1px] border-slate-300 rounded-lg text-[13px] font-bold px-2 py-1"
              >
                {lottoTypes
                  .filter((lt) => LOTTO_TYPE_KEY_BY_TYPE[lt.type])
                  .map((lt) => (
                    <option key={lt.id} value={LOTTO_TYPE_KEY_BY_TYPE[lt.type]}>
                      {lt.name}
                    </option>
                  ))}
              </select>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="md:w-auto bg-amber-100 p-2 h-auto border-[1px] border-slate-300 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("login_username_label")}
                    </span>
                    <div className="flex items-center">
                      <span className="px-2 py-1 bg-white border-b-2 rounded-l-lg font-bold text-xs text-gray-700 select-none">
                        {prefix}
                      </span>
                      {[
                        { val: suffix1, set: setSuffix1 },
                        { val: suffix2, set: setSuffix2 },
                      ].map((s, i) => (
                        <select
                          key={i}
                          value={s.val}
                          onChange={(e) => s.set(e.target.value)}
                          className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-none focus:outline-none focus:border-blue-500"
                        >
                          {[
                            ...Array.from({ length: 10 }, (_, i) => String(i)),
                            ...Array.from({ length: 26 }, (_, i) =>
                              String.fromCharCode(65 + i),
                            ),
                          ].map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("login_password_label")}
                    </span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agent_col_position")}
                    </span>
                    <select
                      id="employee"
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                      name="employee"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                    >
                      <option value=""></option>
                      {pos === "owner" || pos === "company" ? (
                        <option value="senior">Senior</option>
                      ) : (
                        ""
                      )}
                      {pos === "owner" ||
                      pos === "company" ||
                      pos === "senior" ? (
                        <option value="master">Master</option>
                      ) : (
                        ""
                      )}
                      {pos === "owner" ||
                      pos === "company" ||
                      pos === "senior" ||
                      pos === "master" ? (
                        <option value="agent">Agent</option>
                      ) : (
                        ""
                      )}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agent_col_upline")}
                    </span>
                    <input
                      type="text"
                      value={user}
                      disabled
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-100 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agentadd_name_label")} ({t("common_optional")})
                    </span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agentadd_tel_label")} ({t("common_optional")})
                    </span>
                    <input
                      type="text"
                      value={tel}
                      onChange={(e) => setTel(e.target.value)}
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agentadd_email_label")} ({t("common_optional")})
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  {canPickCurrency && (
                    <div className="flex flex-col">
                      <span className="text-xs font-bold">
                        {t("agentadd_currency_label")}
                      </span>
                      <select
                        value={currencyId}
                        onChange={(e) => setCurrencyId(Number(e.target.value))}
                        className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                      >
                        {currencies
                          .filter((c) => c.status)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.code} - {c.name} ({c.symbol})
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-xs font-bold">
                      {t("agentadd_status_label")}
                    </span>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full text-xs px-2 py-1 text-gray-700 bg-slate-50 border-b-[2px] rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="active">
                        {t("agentadd_status_active")}
                      </option>
                      <option value="inactive">
                        {t("agentadd_status_inactive")}
                      </option>
                      <option value="suspend">
                        {t("agentadd_status_suspend")}
                      </option>
                    </select>
                  </div>
                  <div className="flex flex-col justify-center">
                    <label className="inline-flex items-center cursor-pointer gap-2 mt-4">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={lock}
                        onChange={(e) => setLock(e.target.checked)}
                      />
                      <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-red-600 transition-colors">
                        <div
                          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            lock ? "translate-x-5" : ""
                          }`}
                        />
                      </div>
                      <span className="text-xs font-bold">
                        {t("agentadd_lock_label")}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="stepper flex items-center justify-center mt-3">
                {STEPS.map(({ label, step }, idx, arr) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <button
                        type="button"
                        onClick={() => setCurrentComponent(step)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                          currentComponent === step
                            ? "bg-brand-blue-600 border-brand-blue-600 text-white"
                            : currentComponent > step
                              ? "bg-brand-blue-300 border-brand-blue-300 text-white"
                              : "bg-white border-gray-300 text-gray-400"
                        }`}
                      >
                        {step}
                      </button>
                      <span
                        className={`text-[11px] mt-1 text-center whitespace-nowrap ${
                          currentComponent === step
                            ? "text-brand-blue-600 font-bold"
                            : currentComponent > step
                              ? "text-brand-blue-400"
                              : "text-gray-400"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {idx < arr.length - 1 && (
                      <div
                        className={`h-[2px] w-12 mb-4 mx-1 transition-colors ${
                          currentComponent > step
                            ? "bg-brand-blue-400"
                            : "bg-gray-300"
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              {currentComponent === 1 && (
                <div className="flex flex-col gap-2">
                  {packages.map((pkg) => {
                    const pkgData = getPkgData(pkg.id);
                    const pkgName =
                      t(pkg.code) !== pkg.code ? t(pkg.code) : pkg.name;
                    return (
                      <div
                        key={pkg.id}
                        className="w-full border-[1px] border-slate-300 rounded-lg bg-white overflow-hidden"
                      >
                        <div className="flex items-center justify-left px-3 py-2 bg-slate-100">
                          <button
                            type="button"
                            onClick={() => togglePackageExpanded(pkg.id)}
                            className="flex items-center gap-1 text-[14px] font-bold"
                          >
                            {isPackageExpanded(pkg.id) ? (
                              <ExpandLessIcon style={{ fontSize: 20 }} />
                            ) : (
                              <ExpandMoreIcon style={{ fontSize: 20 }} />
                            )}
                            {pkgName}
                          </button>
                          <label className="inline-flex items-center cursor-pointer ml-4">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={pkgData.enabled}
                              onChange={() => togglePackageEnabled(pkg.id)}
                            />
                            <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-600 transition-colors">
                              <div
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                  pkgData.enabled ? "translate-x-5" : ""
                                }`}
                              />
                            </div>
                          </label>
                        </div>
                        {isPackageExpanded(pkg.id) && (
                          <div className="p-2">
                            <Step1
                              isThaiTableVisible={isThaiTableVisible}
                              rewardTypeGroups={activeRewardTypeGroups}
                              payrateUpdate={
                                pkgData.payrate[selectedLotto] ?? {}
                              }
                              setPayrateUpdate={makePkgFieldSetter(
                                pkg.id,
                                "payrate",
                                selectedLotto,
                              )}
                              payrateUpline={
                                pkgData.payrateUpline[selectedLotto] ?? {}
                              }
                              commission={
                                pkgData.commission[selectedLotto] ?? {}
                              }
                              setCommission={makePkgFieldSetter(
                                pkg.id,
                                "commission",
                                selectedLotto,
                              )}
                              commissionUpline={
                                pkgData.commissionUpline[selectedLotto] ?? {}
                              }
                              handleChangeObj={handleChangeObj}
                              handleChangepayrateObj={handleChangepayrateObj}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {currentComponent === 2 && (
                <Step2
                  isThaiTableVisible={isThaiTableVisible}
                  uplineCurrencySymbol={uplineCurrencySymbol}
                  rewardTypeGroups={activeRewardTypeGroups}
                  minbetUpdate={activeBetLimit.minbetUpdate}
                  setMinbetUpdate={activeBetLimit.setMinbetUpdate}
                  minbetUpline={activeBetLimit.minbetUpline}
                  maxbetUpdate={activeBetLimit.maxbetUpdate}
                  setMaxbetUpdate={activeBetLimit.setMaxbetUpdate}
                  maxbetUpline={activeBetLimit.maxbetUpline}
                  maxbetpermatchUpdate={activeBetLimit.maxbetpermatchUpdate}
                  setMaxbetpermatchUpdate={
                    activeBetLimit.setMaxbetpermatchUpdate
                  }
                  maxbetpermatchUpline={activeBetLimit.maxbetpermatchUpline}
                  handleChangepayrateObj={handleChangepayrateObj}
                />
              )}
              {currentComponent === 3 && (
                <Step3
                  isThaiTableVisible={isThaiTableVisible}
                  rewardTypeGroups={activeRewardTypeGroups}
                  maxfight={activeMaxFight.maxfight}
                  setMaxfight={activeMaxFight.setMaxfight}
                  maxfightUpline={activeMaxFight.maxfightUpline}
                  handleChangeObj={handleChangeObj}
                />
              )}
              {currentComponent === 4 && (
                <Step4
                  isThaiTableVisible={isThaiTableVisible}
                  uplineCurrencySymbol={uplineCurrencySymbol}
                  rewardTypeGroups={activeRewardTypeGroups}
                  fightlimit={activeFightLimit.fightlimit}
                  setFightlimit={activeFightLimit.setFightlimit}
                  fightlimitUpline={activeFightLimit.fightlimitUpline}
                  handleChangeObj={handleChangeFightLimit}
                />
              )}
              <div className="flex flex-row space-x-2 mb-1 mt-3">
                {currentComponent < 4 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                  >
                    {t("common_next")}{" "}
                    <ArrowForwardIosIcon style={{ fontSize: 20 }} />
                  </button>
                )}
                {currentComponent !== 1 && (
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="w-[100%] h-[2rem] bg-brand-blue-600 mr-1 hover:bg-brand-blue-700 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                  >
                    <ArrowBackIosNewIcon style={{ fontSize: 20 }} />{" "}
                    {t("common_previous")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-[100%] h-[2rem] bg-green-600 mr-1 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      {t("common_saving")}
                    </>
                  ) : (
                    <>
                      <SaveAltIcon style={{ fontSize: 20 }} />{" "}
                      {t("agentadd_save_button")}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  className="w-[100%] h-[2rem] bg-slate-400 hover:bg-slate-500 text-white text-[14px] font-bold py-1 px-2 rounded flex items-center justify-center gap-1"
                >
                  <CancelIcon style={{ fontSize: 20 }} /> {t("common_cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Limitnumber;
